import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, Users, BookOpen, UserCog, Video, BarChart3,
  Gamepad2, FileText, PenTool, Languages, ClipboardList, Calendar,
  TrendingUp, TrendingDown, Minus, Download, Eye, ChevronLeft,
  ChevronRight, Loader2, User, GraduationCap
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const MonthlyReports = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentReport, setStudentReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  
  // Month/Year selection
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
    { path: '/tutoring', label: 'Live Sessions', icon: Video },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/assessments', label: 'Assessments', icon: ClipboardList },
    { path: '/admin/reports', label: 'Monthly Reports', icon: Calendar },
    { divider: true, label: 'Content Preview' },
    { path: '/admin/preview/games', label: 'Games Zone', icon: Gamepad2 },
    { path: '/admin/preview/exams', label: 'Exams', icon: FileText },
    { path: '/admin/preview/essays', label: 'Essays', icon: PenTool },
    { path: '/admin/preview/esol-videos', label: 'ESOL Videos', icon: Languages },
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchReports();
  }, [month, year]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/lesson-plans/reports/all-students?month=${month}&year=${year}`);
      setReports(res.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    }
    setLoading(false);
  };

  const fetchStudentReport = async (studentId) => {
    setReportLoading(true);
    setSelectedStudent(studentId);
    try {
      const res = await api.get(`/lesson-plans/reports/monthly/${studentId}?month=${month}&year=${year}`);
      setStudentReport(res.data);
    } catch (err) {
      console.error('Failed to fetch student report:', err);
    }
    setReportLoading(false);
  };

  const changeMonth = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    
    setMonth(newMonth);
    setYear(newYear);
    setSelectedStudent(null);
    setStudentReport(null);
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="w-4 h-4 text-emerald-600" />;
    if (trend === 'declining') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getPerformanceBadge = (perf) => {
    const colors = {
      excellent: 'bg-emerald-100 text-emerald-700',
      good: 'bg-blue-100 text-blue-700',
      needs_improvement: 'bg-amber-100 text-amber-700'
    };
    const labels = {
      excellent: 'Excellent',
      good: 'Good',
      needs_improvement: 'Needs Work'
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${colors[perf] || colors.good}`}>
        {labels[perf] || perf}
      </span>
    );
  };

  const downloadPDF = () => {
    // For now, we'll create a printable view
    window.print();
  };

  return (
    <ProLayout sidebarItems={sidebarItems} title="Monthly Reports">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Monthly Progress Reports</h1>
        <p className="text-slate-600">Review student performance and generate reports for parents</p>
      </div>

      {/* Month Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
        <div className="flex items-center justify-between">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => changeMonth(-1)}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          
          <div className="text-center">
            <h2 className="text-xl font-bold text-slate-800">
              {months[month - 1]} {year}
            </h2>
            <p className="text-sm text-slate-500">
              {reports?.total_students || 0} students • {reports?.active_students || 0} active
            </p>
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => changeMonth(1)}
            className="flex items-center gap-1"
            disabled={month === now.getMonth() + 1 && year === now.getFullYear()}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Students List */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-800">Students</h3>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600 mx-auto" />
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {reports?.students?.map((student) => (
                <button
                  key={student.student_id}
                  onClick={() => fetchStudentReport(student.student_id)}
                  className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                    selectedStudent === student.student_id ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''
                  }`}
                  data-testid={`student-${student.student_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-800">{student.student_name}</p>
                      <p className="text-sm text-slate-500">Year {student.year_group}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">{student.lessons_completed}</p>
                      <p className="text-xs text-slate-500">lessons</p>
                    </div>
                  </div>
                  {student.average_score > 0 && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                        <div 
                          className="bg-emerald-500 h-1.5 rounded-full"
                          style={{ width: `${student.average_score}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-slate-600">{student.average_score}%</span>
                    </div>
                  )}
                </button>
              ))}
              
              {reports?.students?.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  No student data for this month
                </div>
              )}
            </div>
          )}
        </div>

        {/* Report Detail */}
        <div className="md:col-span-2">
          {reportLoading ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto" />
              <p className="text-slate-500 mt-2">Loading report...</p>
            </div>
          ) : studentReport ? (
            <div className="space-y-4 print:space-y-2" id="printable-report">
              {/* Report Header */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 print:shadow-none print:border-0">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-800">{studentReport.student_name}</h2>
                      <div className="flex items-center gap-2 text-slate-500">
                        <GraduationCap className="w-4 h-4" />
                        <span>Year {studentReport.year_group} • {studentReport.key_stage?.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                  <Button 
                    onClick={downloadPDF}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2 print:hidden"
                  >
                    <Download className="w-4 h-4" />
                    Print Report
                  </Button>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600">Monthly Progress Report</p>
                  <p className="font-bold text-lg text-slate-800">{studentReport.report_period?.month_name}</p>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4 print:grid-cols-4">
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 print:shadow-none">
                  <p className="text-sm text-slate-500">Lessons Completed</p>
                  <p className="text-2xl font-bold text-slate-800">{studentReport.summary?.total_lessons_completed}</p>
                  <p className="text-xs text-slate-400">of {studentReport.summary?.total_lessons_assigned} assigned</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 print:shadow-none">
                  <p className="text-sm text-slate-500">Completion Rate</p>
                  <p className="text-2xl font-bold text-emerald-600">{studentReport.summary?.completion_rate}%</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 print:shadow-none">
                  <p className="text-sm text-slate-500">Average Score</p>
                  <p className="text-2xl font-bold text-blue-600">{studentReport.summary?.average_score}%</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 print:shadow-none">
                  <p className="text-sm text-slate-500">Trend</p>
                  <div className="flex items-center gap-2">
                    {getTrendIcon(studentReport.summary?.performance_trend)}
                    <span className="text-lg font-semibold capitalize">{studentReport.summary?.performance_trend}</span>
                  </div>
                </div>
              </div>

              {/* Subject Breakdown */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 print:shadow-none">
                <h3 className="font-bold text-slate-800 mb-4">Subject Performance</h3>
                <div className="space-y-4">
                  {studentReport.subject_breakdown?.map((subject) => (
                    <div key={subject.subject} className="flex items-center gap-4">
                      <div className="w-24">
                        <p className="font-medium text-slate-800 capitalize">{subject.subject}</p>
                        <p className="text-xs text-slate-500">{subject.lessons_completed} lessons</p>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-100 rounded-full h-3">
                            <div 
                              className={`h-3 rounded-full ${
                                subject.average_score >= 85 ? 'bg-emerald-500' :
                                subject.average_score >= 70 ? 'bg-blue-500' : 'bg-amber-500'
                              }`}
                              style={{ width: `${subject.average_score}%` }}
                            />
                          </div>
                          <span className="font-bold text-slate-800 w-12">{subject.average_score}%</span>
                        </div>
                      </div>
                      {getPerformanceBadge(subject.performance)}
                    </div>
                  ))}
                  
                  {studentReport.subject_breakdown?.length === 0 && (
                    <p className="text-slate-500 text-center py-4">No subject data for this month</p>
                  )}
                </div>
              </div>

              {/* Recommendations */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 print:shadow-none">
                <h3 className="font-bold text-slate-800 mb-4">Recommendations</h3>
                <ul className="space-y-2">
                  {studentReport.recommendations?.map((rec, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-emerald-600 text-xs font-bold">{i + 1}</span>
                      </div>
                      <p className="text-slate-700">{rec}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 rounded-xl p-4 text-center print:mt-8">
                <p className="text-sm text-slate-500">
                  Report generated on {new Date(studentReport.generated_at).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
                <p className="text-xs text-slate-400 mt-1">Growing Minds Tuition</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-800 mb-1">Select a Student</h3>
              <p className="text-slate-500">Click on a student to view their monthly report</p>
            </div>
          )}
        </div>
      </div>
    </ProLayout>
  );
};

export default MonthlyReports;
